/**
 * EDUNA Logo Component
 * Modern, clean SVG logo for the platform
 * 
 * Usage:
 * <EdunaLogo /> - Default with icon + text
 * <EdunaLogo iconOnly /> - Just the icon
 * <EdunaLogo size={48} /> - Custom size
 * <EdunaLogo color="#ffffff" /> - Custom color
 */

const EdunaLogo = ({ 
  size = 32, 
  iconOnly = false, 
  color = 'currentColor',
  textColor,
  className = '' 
}) => {
  const actualTextColor = textColor || color;
  
  // Calculate text size proportionally
  const textSize = size * 0.75;
  
  return (
    <div className={`eduna-logo ${className}`} style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.25 }}>
      {/* Icon - Graduation cap with book/shield hybrid */}
      <svg 
        width={size} 
        height={size} 
        viewBox="0 0 48 48" 
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="edunaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#667eea" />
            <stop offset="100%" stopColor="#764ba2" />
          </linearGradient>
          <linearGradient id="edunaGradientLight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
        
        {/* Main shape - Stylized "E" with graduation cap feel */}
        <path 
          d="M24 4L6 14v4l18 10 18-10v-4L24 4z" 
          fill={color === 'currentColor' ? 'url(#edunaGradient)' : color}
        />
        
        {/* Book/base element */}
        <path 
          d="M10 22v12c0 2 1 4 3 5l11 5 11-5c2-1 3-3 3-5V22l-14 8-14-8z" 
          fill={color === 'currentColor' ? 'url(#edunaGradientLight)' : color}
          opacity="0.85"
        />
        
        {/* Center accent - represents knowledge/growth */}
        <circle 
          cx="24" 
          cy="18" 
          r="4" 
          fill="white"
          opacity="0.9"
        />
        
        {/* Tassel/accent line */}
        <path 
          d="M24 22v10M24 32l-3 4M24 32l3 4" 
          stroke={color === 'currentColor' ? '#667eea' : color}
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.7"
        />
      </svg>
      
      {/* Text */}
      {!iconOnly && (
        <span 
          style={{ 
            fontSize: textSize,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: actualTextColor,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}
        >
          EDUNA
        </span>
      )}
    </div>
  );
};

// Alternative simpler icon version
export const EdunaIcon = ({ size = 32, color = 'currentColor' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="edunaIconGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#667eea" />
        <stop offset="100%" stopColor="#764ba2" />
      </linearGradient>
    </defs>
    
    {/* Graduation cap top */}
    <path 
      d="M24 4L6 14l18 10 18-10L24 4z" 
      fill={color === 'currentColor' ? 'url(#edunaIconGrad)' : color}
    />
    
    {/* Cap base/book */}
    <path 
      d="M10 20v14c0 2 1.5 4 4 5l10 5 10-5c2.5-1 4-3 4-5V20L24 30 10 20z" 
      fill={color === 'currentColor' ? 'url(#edunaIconGrad)' : color}
      opacity="0.7"
    />
    
    {/* Center dot */}
    <circle cx="24" cy="16" r="3" fill="white" />
  </svg>
);

// Horizontal logo with full wordmark
export const EdunaLogoFull = ({ height = 40, color = 'currentColor' }) => {
  const width = height * 4;
  
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 160 40" 
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="edunaFullGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#764ba2" />
        </linearGradient>
      </defs>
      
      {/* Icon */}
      <g transform="translate(0, 0)">
        <path 
          d="M20 2L4 10l16 9 16-9L20 2z" 
          fill="url(#edunaFullGrad)"
        />
        <path 
          d="M6 14v12c0 1.5 1 3 3 4l11 5 11-5c2-1 3-2.5 3-4V14L20 23 6 14z" 
          fill="url(#edunaFullGrad)"
          opacity="0.7"
        />
        <circle cx="20" cy="12" r="2.5" fill="white" />
      </g>
      
      {/* Text */}
      <text 
        x="48" 
        y="28" 
        fill={color === 'currentColor' ? '#1f2937' : color}
        fontFamily="Inter, -apple-system, BlinkMacSystemFont, sans-serif"
        fontSize="24"
        fontWeight="700"
        letterSpacing="-0.5"
      >
        EDUNA
      </text>
    </svg>
  );
};

export default EdunaLogo;
